<template>
    <div>
        <div class="md:flex md:items-center md:justify-between">
            <div class="min-w-0 flex-1">
                <router-link
                    :to="{ name: 'editVenue' }"
                    class="mb-2 flex items-center text-sm text-gray-500 inline-flex"
                >
                    <base-back-icon />
                    <span>Settings</span>
                </router-link>
                <base-page-title title="Automations" />
            </div>
            <div
                class="mr-4 mt-4 flex md:ml-4 md:mt-0"
                v-if="!isLoading && syncSchedules && syncSchedules.length"
            >
                <div class="md:ml-3">
                    <base-button @clicked="addSyncSchedule"
                                 buttonText="Add sync schedule"
                                 :disabled="isLoading"
                                 button-style="action"
                                 size="sm"
                    />
                </div>
            </div>
            <BaseSpinner :isLoading="isLoading" />
        </div>
        <div>
            <div class="mt-6 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="sm:pl-0 sm:pr-5">
                        <h3
                            class="text-base font-semibold leading-6 text-gray-800"
                            >Sync Schedules</h3
                        >
                        <p class="mt-2 text-sm text-gray-500">
                            Create automated synchronization schedules for your store.
                            Configure when and how your store data should be synchronized.
                        </p>
                    </div>
                </div>
                <div
                    class="mt-5 md:col-span-2 md:mt-0"
                    :class="{ 'h-full': !syncSchedules.length }"
                >
                        <div
                            class="handle col-span-6 mb-8 overflow-hidden rounded-lg bg-white shadow sm:overflow-hidden sm:rounded-md"
                            v-for="(schedule, index) in syncSchedules"
                            :key="schedule.id || index"
                        >
                            <div class="px-4 py-5 sm:p-6">
                                <div
                                    class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4"
                                >
                                    <label
                                        :for="'label-' + (schedule.id || index)"
                                        class="block text-sm font-medium leading-5 text-gray-700 sm:mt-px sm:pt-2"
                                    >
                                        Name
                                    </label>
                                    <div class="mt-1 sm:col-span-2 sm:mt-0">
                                        <div
                                            class="flex max-w-lg rounded-md shadow-sm"
                                        >
                                            <input
                                                :id="'label-' + (schedule.id || index)"
                                                v-model.trim="schedule.label"
                                                :class="{
                                                    'border-red-300':
                                                        $v.syncSchedules.$each[index]
                                                            .label.$error
                                                }"
                                                class="form-input block w-full flex-1 rounded-md transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                placeholder="e.g. Daily menu sync"
                                            />
                                        </div>
                                        <span
                                            class="text-xs text-red-500"
                                            v-if="
                                                $v.syncSchedules.$each[index].label
                                                    .$error &&
                                                !$v.syncSchedules.$each[index].label
                                                    .required
                                            "
                                        >
                                            Name is required.
                                        </span>
                                    </div>
                                </div>

                                <div
                                    class="mt-3 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:pt-2"
                                >
                                    <label
                                        :for="'kind-' + (schedule.id || index)"
                                        class="block text-sm font-medium leading-5 text-gray-700 sm:mt-px sm:pt-2"
                                    >
                                        Kind
                                    </label>
                                    <div class="mt-1 sm:col-span-2 sm:mt-0">
                                        <div
                                            class="rounded-md shadow-sm"
                                        >
                                            <select
                                                :id="'kind-' + (schedule.id || index)"
                                                v-model="schedule.kind"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            >
                                                <option value="menu">Menu</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    class="mt-3 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4"
                                >
                                    <label
                                        :for="'cron-' + (schedule.id || index)"
                                        class="block text-sm font-medium leading-5 text-gray-700 sm:mt-px sm:pt-2"
                                    >
                                        Schedule
                                    </label>
                                    <div class="mt-1 sm:col-span-2 sm:mt-0">
                                        <div
                                            v-if="!isEditingCron(schedule.id, index)"
                                            class="flex items-center justify-between rounded-md border border-gray-300 bg-gray-50 px-3 py-2"
                                        >
                                            <div class="flex-1">
                                                <span class="text-sm text-gray-900">
                                                    {{ getHumanReadableCron(schedule.cron_expr) }}
                                                </span>
                                                <div v-if="schedule.cron_expr" class="text-xs text-gray-500 font-mono mt-1">
                                                    {{ schedule.cron_expr }}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                @click="startEditingCron(schedule.id, index)"
                                                class="ml-3 text-sm text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline"
                                            >
                                                Edit
                                            </button>
                                        </div>
                                        
                                        <div
                                            v-else
                                            class="flex max-w-lg rounded-md shadow-sm"
                                        >
                                            <input
                                                :id="'cron-' + (schedule.id || index)"
                                                v-model.trim="schedule.cron_expr"
                                                :class="{
                                                    'border-red-300':
                                                        $v.syncSchedules.$each[index]
                                                            .cron_expr.$error
                                                }"
                                                class="form-input block w-full flex-1 rounded-l-md transition duration-150 ease-in-out sm:text-sm sm:leading-5 font-mono"
                                                placeholder="0 9 * * * (daily at 9 AM)"
                                                @keyup.enter="stopEditingCron(schedule.id, index)"
                                                @blur="stopEditingCron(schedule.id, index)"
                                            />
                                            <button
                                                type="button"
                                                @click="stopEditingCron(schedule.id, index)"
                                                class="relative -ml-px inline-flex items-center rounded-r-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                            >
                                                Done
                                            </button>
                                        </div>
                                        
                                        <span
                                            class="text-xs text-red-500"
                                            v-if="
                                                $v.syncSchedules.$each[index].cron_expr
                                                    .$error &&
                                                !$v.syncSchedules.$each[index].cron_expr
                                                    .required
                                            "
                                        >
                                            Cron expression is required.
                                        </span>
                                        
                                        <!-- Next run time display -->
                                        <div
                                            v-if="schedule.next_run_at"
                                            class="mt-2 text-xs text-gray-600"
                                        >
                                            <span class="font-medium">Next run:</span>
                                            {{ formatNextRunTime(schedule.next_run_at) }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                class="flex justify-end border-t border-gray-200 bg-gray-100 px-4 py-2 sm:px-6"
                            >
                                <div>
                                    <span
                                        class="flex w-full rounded-md hover:shadow-sm sm:ml-3 sm:w-auto"
                                        @click="deleteSchedule(schedule, index)"
                                    >
                                        <button
                                            type="button"
                                            class="leading-2 focus:outline-none inline-flex w-full justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-500 transition duration-150 ease-in-out hover:bg-red-500 hover:text-white hover:shadow-sm focus:border-red-700 focus:shadow-outline-red sm:text-sm sm:leading-5"
                                        >
                                            Delete
                                        </button>
                                    </span>
                                </div>
                            </div>
                        </div>
                    <div
                        v-if="!isLoading && !syncSchedules.length"
                        class="col-span-6 mb-2 flex h-full flex-col items-center justify-center overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:overflow-hidden sm:rounded-md sm:p-5"
                    >
                        <div>
                            <p class="mb-4 font-semibold text-gray-700"
                                >No sync schedules yet</p
                            >
                        </div>
                        <div class="mr-4 mt-4 flex md:ml-4 md:mt-0">
                              <base-button @clicked="addSyncSchedule"
                                           buttonText="Add sync schedule"
                                           button-style="action"
                                           :disabled="isLoading"
                                           size="sm"
                              />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <BaseSpacerWithRuler />

        <div
            class="my-6 ml-4 flex flex-shrink-0 justify-end"
            v-if="syncSchedules && syncSchedules.length"
        >
            <span class="inline-flex rounded-md shadow-sm" @click="saveSchedules">
                <button
                    type="button"
                    class="focus:outline-none relative inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium leading-5 text-white hover:bg-indigo-500 focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700"
                    :disabled="isLoading || $v.$invalid"
                >
                    Save
                </button>
            </span>
        </div>
    </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import cronstrue from 'cronstrue';

export default {
    name: 'AutomationsSettings',
    props: ['venueId'],
    metaInfo: {
        title: 'Automations'
    },
    data() {
        return {
            syncSchedules: [],
            isLoading: true,
            editingCron: {}
        };
    },
    validations: {
        syncSchedules: {
            $each: {
                label: { required },
                kind: { required },
                cron_expr: { required }
            }
        }
    },
    computed: {
        ...mapGetters({
            venue: 'venues/getVenue'
        }),
        computedVenueId() {
            return this.venueId || this.venue?.id || this.$route.params.venueId;
        }
    },
    created() {
        this.fetchSyncSchedules();
    },
    methods: {
        getHumanReadableCron(cronExpr) {
            if (!cronExpr || cronExpr.trim() === '') {
                return 'No schedule set';
            }
            try {
                return cronstrue.toString(cronExpr);
            } catch (error) {
                return cronExpr;
            }
        },
        startEditingCron(scheduleId, index) {
            const key = scheduleId || `new-${index}`;
            this.$set(this.editingCron, key, true);
        },
        stopEditingCron(scheduleId, index) {
            const key = scheduleId || `new-${index}`;
            this.$set(this.editingCron, key, false);
        },
        isEditingCron(scheduleId, index) {
            const key = scheduleId || `new-${index}`;
            return this.editingCron[key] || false;
        },
        formatNextRunTime(nextRunAt) {
            if (!nextRunAt) {
                return '';
            }
            try {
                const date = new Date(nextRunAt);
                return date.toLocaleString('en-US', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short'
                });
            } catch (error) {
                return nextRunAt;
            }
        },
        async fetchSyncSchedules() {
            this.isLoading = true;

            try {
                const { data } = await this.$axios.get(
                    `/venues/${this.computedVenueId}/sync-schedules`
                );

                this.syncSchedules = data;
            } catch (e) {
                this.$notify({
                    group: 'settings',
                    title: 'Error loading sync schedules',
                    text: e.response?.data?.message || 'Failed to load sync schedules'
                });
            } finally {
                this.isLoading = false;
            }
        },
        addSyncSchedule() {
            const newIndex = this.syncSchedules.length;
            this.syncSchedules.push({
                kind: 'menu',
                label: '',
                cron_expr: ''
            });
            this.startEditingCron(null, newIndex);
        },
        async saveSchedules() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return false;
            }

            this.isLoading = true;

            try {
                const schedules = this.syncSchedules.map(
                    // eslint-disable-next-line no-unused-vars
                    ({ created_at, updated_at, ...schedule }) => schedule
                );

                for (const schedule of schedules) {
                    if (schedule.id) {
                        // For updates, only send editable properties
                        const updatePayload = {
                            label: schedule.label,
                            kind: schedule.kind,
                            cron_expr: schedule.cron_expr
                        };
                        await this.$axios.put(
                            `/venues/${this.computedVenueId}/sync-schedules/${schedule.id}`,
                            updatePayload
                        );
                    } else {
                        // For creates, send all properties (excluding id, timestamps)
                        const { data } = await this.$axios.post(
                            `/venues/${this.computedVenueId}/sync-schedules`,
                            schedule
                        );
                        schedule.id = data.id;
                    }
                }

                this.$notify({
                    group: 'settings',
                    title: 'Sync schedules updated'
                });
                
                await this.fetchSyncSchedules();
            } catch (e) {
                this.$notify({
                    group: 'settings',
                    title: 'Error saving sync schedules',
                    text: e.response?.data?.message || 'Save operation failed'
                });
            } finally {
                this.isLoading = false;
            }
        },

        async deleteSchedule(schedule, index) {
            this.syncSchedules = this.syncSchedules.filter((val, i) => i !== index);

            if (!schedule.id) {
                return;
            }

            this.isLoading = true;

            try {
                await this.$axios.delete(
                    `/venues/${this.computedVenueId}/sync-schedules/${schedule.id}`
                );

                await this.fetchSyncSchedules();
            } catch (e) {
                this.$notify({
                    group: 'settings',
                    title: 'Error deleting sync schedule',
                    text: e.response?.data?.message || 'Delete operation failed'
                });
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>
